import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    if (this.#isDetailsContentEmpty()) {
      this.style.display = 'none';
      return;
    }

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Checks if the details-content element is empty
   * @returns {boolean} True if details-content is empty, false otherwise
   */
  #isDetailsContentEmpty() {
    const detailsContent = this.querySelector('.details-content');
    
    if (!detailsContent) {
      const details = this.querySelector('details');
      if (!details) return true;
      
      const contentChildren = Array.from(details.children).filter(child => 
        child.tagName.toLowerCase() !== 'summary'
      );
      
      if (contentChildren.length === 0) return true;
      
      return contentChildren.every(child => this.#isEmptyElement(child));
    }
    
    return this.#isEmptyElement(detailsContent);
  }

  /**
   * Checks if an element is empty (no text content after removing spaces)
   * @param {Element} element - The element to check
   * @returns {boolean} True if element is empty, false otherwise
   */
  #isEmptyElement(element) {
    const textContent = element.textContent?.trim();
    if (textContent && textContent.length > 0) return false;
    
    const htmlContent = element.innerHTML?.trim();
    if (htmlContent && htmlContent.length > 0) {
      const hasContent = /<[^>]*>.*<\/[^>]*>|<[^>]*\/>/.test(htmlContent) || 
                        /[^\s<>]/.test(htmlContent);
      if (hasContent) return false;
    }
    
    const hasInteractiveElements = element.querySelector('input, textarea, select, button, img, video, audio, iframe, object, embed');
    if (hasInteractiveElements) return false;
    
    const hasContentAttributes = element.querySelector('[src], [href]');
    if (hasContentAttributes) return false;
    
    const allElements = element.querySelectorAll('*');
    const hasDataOrAriaAttributes = Array.from(allElements).some((el) => {
      const attributes = Array.from(el.attributes);
      return attributes.some(attr => 
        attr.name.startsWith('data-') || attr.name.startsWith('aria-')
      );
    });
    if (hasDataOrAriaAttributes) return false;
    
    return true;
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
